import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  Circle,
  Building2,
  Globe,
  BarChart3,
  Target,
  Users,
  Share2,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { api, useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

// Import step components
import WebsiteAccessStep from './onboarding/WebsiteAccessStep';
import AnalyticsSetupStep from './onboarding/AnalyticsSetupStep';
import GoalsStep from './onboarding/GoalsStep';
import TeamStep from './onboarding/TeamStep';
import BaselineCapture from './onboarding/BaselineCapture';
import SocialAccountsStep from './onboarding/SocialAccountsStep';

const BASE_STEPS = [
  { id: 'business_info', label: 'Business Info', icon: Building2, completed: true },
  { id: 'website_access', label: 'Website Access', icon: Globe },
  { id: 'analytics_setup', label: 'Analytics Setup', icon: BarChart3 },
  { id: 'social_accounts', label: 'Social Accounts', icon: Share2 },
  { id: 'goals', label: 'Goals', icon: Target },
];

const TEAM_STEP = { id: 'team', label: 'Team', icon: Users };

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentStepIndex, setCurrentStepIndex] = useState(1); // Start at website_access (index 1)
  const [onboardingData, setOnboardingData] = useState(null);
  const [showBaseline, setShowBaseline] = useState(false);

  // Only show the Team step when the user has an active package assigned.
  // All GDS packages are managed services, but a newly registered user may not
  // have a package_type yet — avoid promising a dedicated team in that case.
  const hasPackage = !!user?.package_type;
  const STEPS = hasPackage ? [...BASE_STEPS, TEAM_STEP] : BASE_STEPS;

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const response = await api.get('/onboarding/status');
      setOnboardingData(response.data);
      
      // If onboarding already completed, redirect to dashboard
      if (response.data.onboarding_completed) {
        // Packageless clients land on billing so they can pick a plan
        if (user?.role === 'client' && !user?.package_type) {
          navigate('/dashboard/billing?from=onboarding');
        } else {
          navigate('/dashboard');
        }
        return;
      }
      
      // Set current step based on backend data.
      // If backend says 'team' but user has no package, fall back to 'goals'.
      let targetStep = response.data.current_step;
      if (targetStep === 'team' && !hasPackage) targetStep = 'goals';
      const stepIndex = STEPS.findIndex(s => s.id === targetStep);
      if (stepIndex >= 0) {
        setCurrentStepIndex(stepIndex);
      }
    } catch (error) {
      console.error('Error fetching onboarding status:', error);
      toast.error('Failed to load onboarding status');
    } finally {
      setLoading(false);
    }
  };

  const goToNextStep = () => {
    if (currentStepIndex < STEPS.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      // Show baseline capture
      setShowBaseline(true);
    }
  };

  const goToPrevStep = () => {
    if (currentStepIndex > 1) { // Can't go back to business_info
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleComplete = async () => {
    try {
      await api.post('/onboarding/complete');
      // refreshUser now returns the fresh user object — use it directly so we
      // don't race against React's async state flush.
      const freshUser = await refreshUser();
      const resolvedUser = freshUser || user;
      toast.success('Welcome to GDS! Your onboarding is complete.');
      if (resolvedUser?.role === 'client' && !resolvedUser?.package_type) {
        navigate('/dashboard/billing?from=onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Failed to complete onboarding');
    }
  };

  const progress = ((currentStepIndex + 1) / STEPS.length) * 100;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (showBaseline) {
    return <BaselineCapture onComplete={handleComplete} />;
  }

  const currentStep = STEPS[currentStepIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-heading font-bold text-lg">GDS</span>
                <p className="text-xs text-slate-500">Onboarding</p>
              </div>
            </div>
            <div className="text-sm text-slate-500">
              Step {currentStepIndex + 1} of {STEPS.length}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {STEPS.map((step, index) => {
              const isCompleted = index < currentStepIndex || onboardingData?.completed_steps?.includes(step.id);
              const isCurrent = index === currentStepIndex;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center transition-all
                      ${isCompleted ? 'bg-emerald-500 text-white' : 
                        isCurrent ? 'bg-indigo-600 text-white ring-4 ring-indigo-100' : 
                        'bg-slate-200 text-slate-500'}
                    `}>
                      {isCompleted ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : (
                        <step.icon className="w-5 h-5" />
                      )}
                    </div>
                    <span className={`text-xs mt-2 font-medium ${isCurrent ? 'text-indigo-600' : 'text-slate-500'}`}>
                      {step.label}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`w-16 md:w-24 h-1 mx-2 rounded ${
                      index < currentStepIndex ? 'bg-emerald-500' : 'bg-slate-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {currentStep.id === 'website_access' && (
              <WebsiteAccessStep 
                data={onboardingData?.website_access}
                onNext={goToNextStep}
                onBack={goToPrevStep}
              />
            )}
            {currentStep.id === 'analytics_setup' && (
              <AnalyticsSetupStep 
                data={onboardingData?.integrations}
                onNext={goToNextStep}
                onBack={goToPrevStep}
                onRefresh={fetchOnboardingStatus}
              />
            )}
            {currentStep.id === 'social_accounts' && (
              <SocialAccountsStep
                onNext={goToNextStep}
                onBack={goToPrevStep}
              />
            )}
            {currentStep.id === 'goals' && (
              <GoalsStep
                data={onboardingData?.goals}
                onNext={goToNextStep}
                onBack={goToPrevStep}
              />
            )}
            {currentStep.id === 'team' && (
              <TeamStep 
                data={onboardingData?.team}
                onNext={goToNextStep}
                onBack={goToPrevStep}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
